import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ExternalLink, GripVertical, Info, Loader, Merge, Plus, RefreshCcwDot, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { FilePreviewDialog, type PreviewFile } from "@/components/files/file-preview-dialog";
import { Button } from "@/components/ui/button";

import type { ClaimWithRelations } from "../../api/use-claim";
import type { useClaimFormalNotice } from "../../api/use-claim-actions";
import {
  useGenerateFormalNoticeProject,
  useMergeFormalNotice,
  usePutFormalNotice,
} from "../../api/use-claim-actions";
import { openBoxFile } from "../../lib/open-box";
import { toPreviewFile } from "../claim-documents";

type FormalNotice = NonNullable<ReturnType<typeof useClaimFormalNotice>["data"]>;
type ClaimDocument = ClaimWithRelations["claimDocuments"][number];

interface OrderedDocument {
  id: string;
  label: string;
  file: ClaimDocument["file"] | undefined;
}

function documentLabel(claimDocument: {
  file?: { name: string | null };
  documentType?: { type: string; displayName: string };
}): string {
  if (!claimDocument.documentType) {
    return claimDocument.file?.name ?? "Document";
  }
  return claimDocument.documentType.type === "OTHER"
    ? (claimDocument.file?.name ?? claimDocument.documentType.displayName)
    : claimDocument.documentType.displayName;
}

/**
 * Default MED exhibit order — prefilled for a new Mise en Demeure so the usual
 * arrangement is one click away (parity with the médiation archive prefill).
 * Only these types are pre-added, in this order; PROOF_OF_EXPENSE lands in the
 * list only when the claim actually has one.
 */
const MED_DOCUMENT_ORDER = [
  "EMAIL_CONVERSATION",
  "ID_PROOF",
  "DEBT_CONTRACT",
  "DEBT_CONTRACT_TRAVEL_AGENCY_TO_UPCLAIM",
  "CLAIM_NOTIFICATION",
  "PROOF_OF_EXPENSE",
  "KBIS_UPCLAIM",
];

function orderedDefaultDocuments(claimDocuments: ClaimDocument[]): OrderedDocument[] {
  return MED_DOCUMENT_ORDER.flatMap((type) =>
    claimDocuments
      .filter((claimDocument) => claimDocument.documentType.type === type)
      .map((claimDocument) => ({
        id: claimDocument.id,
        label: documentLabel(claimDocument),
        file: claimDocument.file,
      })),
  );
}

function SortableFileRow({
  file,
  index,
  onRemove,
  onPreview,
}: {
  file: OrderedDocument;
  index: number;
  onRemove: () => void;
  onPreview: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: file.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="flex cursor-grab touch-none items-center justify-between py-4 pr-5 text-sm leading-6 select-none"
    >
      <div className="ml-4 flex min-w-0 flex-1 items-center gap-2">
        <GripVertical className="h-4 w-4 shrink-0 text-gray-400" aria-hidden="true" />
        <span className="inline-flex items-center rounded-full bg-pink-50 px-2 py-1 text-xs font-medium text-pink-700 ring-1 ring-pink-700/10 ring-inset">
          Pièce № {index + 1}
        </span>
        <button
          type="button"
          onClick={onPreview}
          className="cursor-pointer truncate text-left font-medium underline-offset-2 hover:underline"
        >
          {file.label}
        </button>
      </div>
      <div className="ml-4 flex shrink-0">
        <button
          type="button"
          onClick={onRemove}
          className="cursor-pointer rounded-md font-medium text-gray-400 hover:text-red-500"
          aria-label="Retirer de la liste"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </li>
  );
}

interface GenerateFormalNoticeProps {
  claim: ClaimWithRelations;
  formalNotice: FormalNotice | undefined;
}

/**
 * Port of overview/GenerateFormalNotice.vue — ordered document list of the MED.
 * Drag-reorder via dnd-kit (restores the Vue vuedraggable UX), click a document
 * to preview it (FilePreviewDialog, same primitive as the Documents tab).
 */
export function GenerateFormalNotice({ claim, formalNotice }: GenerateFormalNoticeProps) {
  const generateProject = useGenerateFormalNoticeProject(claim.id);
  const mergeFormalNotice = useMergeFormalNotice(claim.id);
  const putFormalNotice = usePutFormalNotice(claim.id);

  const [filesIn, setFilesIn] = useState<OrderedDocument[]>([]);
  const [hasChange, setHasChange] = useState(false);
  const [previewedFile, setPreviewedFile] = useState<PreviewFile | null>(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
  const prefilledRef = useRef(false);

  useEffect(() => {
    if (formalNotice) {
      setFilesIn(
        (formalNotice.formalNoticeDocuments ?? []).flatMap(({ claimDocument }) =>
          claimDocument
            ? [{ id: claimDocument.id, label: documentLabel(claimDocument), file: claimDocument.file }]
            : [],
        ),
      );
      setHasChange(false);
      return;
    }
    // New MED: seed the list once with the default exhibit order; guard against
    // clobbering the user's arrangement on a background claim refetch.
    if (prefilledRef.current) {
      return;
    }
    prefilledRef.current = true;
    setFilesIn(orderedDefaultDocuments(claim.claimDocuments));
  }, [formalNotice, claim.claimDocuments]);

  const availableDocuments = claim.claimDocuments.filter(
    (claimDocument) => !filesIn.some((file) => file.id === claimDocument.id),
  );

  function addDocument(claimDocument: ClaimDocument) {
    setFilesIn((files) => [
      ...files,
      { id: claimDocument.id, label: documentLabel(claimDocument), file: claimDocument.file },
    ]);
    setHasChange(true);
  }

  function removeFromOrder(index: number) {
    setFilesIn((files) => files.filter((_, i) => i !== index));
    setHasChange(true);
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }
    setFilesIn((files) => {
      const oldIndex = files.findIndex((file) => file.id === active.id);
      const newIndex = files.findIndex((file) => file.id === over.id);
      return arrayMove(files, oldIndex, newIndex);
    });
    setHasChange(true);
  }

  async function generateFormalNoticeProject() {
    if (!filesIn.length) {
      return;
    }
    // Pre-open the tab inside the click gesture: the generation takes 1-2 min, and a
    // window.open that late gets popup-blocked. notify.promise already toasts any error.
    const boxTab = window.open("about:blank", "_blank");
    const result = await generateProject.mutateAsync(filesIn.map(({ id }) => id)).catch(() => null);
    if (result?.docxBoxFileId) {
      openBoxFile(result.docxBoxFileId, boxTab);
    } else {
      boxTab?.close();
    }
  }

  return (
    <div>
      <FilePreviewDialog
        open={previewedFile !== null}
        file={previewedFile}
        onClose={() => setPreviewedFile(null)}
      />

      <div>
        <h3 className="mb-2 text-sm leading-7 font-medium">Documents dans la Mise en Demeure</h3>
        <ul className="col-span-3 mb-2 w-full divide-y divide-blue-50 rounded-md border border-dashed border-blue-200 bg-white shadow-sm">
          {!filesIn.length ? (
            <li className="flex items-center justify-between py-4 pr-5 pl-4 text-sm leading-6">
              <div className="flex w-0 flex-1 items-center">
                <Info className="ml-2 h-5 w-5 shrink-0 text-gray-400" aria-hidden="true" />
                <span className="ml-4 flex items-center truncate font-medium">
                  Aucun document pour l'instant
                </span>
              </div>
            </li>
          ) : null}
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={filesIn.map((file) => file.id)} strategy={verticalListSortingStrategy}>
              {filesIn.map((file, index) => (
                <SortableFileRow
                  key={file.id}
                  file={file}
                  index={index}
                  onRemove={() => removeFromOrder(index)}
                  onPreview={() => file.file && setPreviewedFile(toPreviewFile(file.file))}
                />
              ))}
            </SortableContext>
          </DndContext>
        </ul>

        {availableDocuments.length ? (
          <ul className="mb-5 w-full divide-y divide-gray-100 rounded-md border border-gray-200 bg-gray-50">
            {availableDocuments.map((claimDocument) => (
              <li
                key={claimDocument.id}
                className="flex items-center justify-between py-2 pr-5 pl-4 text-sm leading-6"
              >
                <button
                  type="button"
                  onClick={() => setPreviewedFile(toPreviewFile(claimDocument.file))}
                  className="cursor-pointer truncate text-left text-gray-600 hover:underline"
                >
                  {documentLabel(claimDocument)}
                </button>
                <button
                  type="button"
                  onClick={() => addDocument(claimDocument)}
                  className="ml-4 cursor-pointer rounded-md font-medium text-blue-600 hover:text-blue-500"
                  aria-label="Ajouter à la Mise en Demeure"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      <div>
        {!formalNotice ? (
          <Button
            onClick={generateFormalNoticeProject}
            disabled={generateProject.isPending || !filesIn.length}
          >
            {generateProject.isPending ? <Loader className="animate-spin" /> : null}
            Générer le projet de MED
          </Button>
        ) : (
          <div className="inline-flex space-x-4">
            <Button
              disabled={hasChange || mergeFormalNotice.isPending}
              onClick={() => mergeFormalNotice.mutate()}
            >
              {mergeFormalNotice.isPending ? <Loader className="animate-spin" /> : <Merge />}
              Fusionner
            </Button>
            {formalNotice.formalNoticeDocxFile?.boxFileId ? (
              <Button
                variant="outline"
                onClick={() => openBoxFile(formalNotice.formalNoticeDocxFile?.boxFileId as string)}
              >
                <ExternalLink />
                Ouvrir le fichier .docx
              </Button>
            ) : null}
            {hasChange ? (
              <Button
                onClick={() => putFormalNotice.mutate(filesIn.map(({ id }) => id))}
                disabled={putFormalNotice.isPending}
              >
                {putFormalNotice.isPending ? <Loader className="animate-spin" /> : <RefreshCcwDot />}
                Rafraîchir
              </Button>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}
